import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import type { ContractBulkPatch, ContractCollectionRecord, ContractStatus } from '@shared/types';
import { subscribeToShellDeleteRequests } from '@renderer/app/events/shell-delete-events';
import { subscribeToShellDomainInvalidations } from '@renderer/app/events/shell-domain-events';
import { InlineQuickAddPanel, createQuickAddContext } from '@renderer/app/quick-add/quick-add-shared';
import { useShellStore } from '@renderer/app/state/shell-store';
import { BulkEditorRail } from '@renderer/shared/components/bulk-editor-rail';
import {
  CollectionColumnSettingsModal,
  resolveVisibleCollectionColumns,
  type CollectionColumnConfig
} from '@renderer/shared/components/collection-column-settings';
import {
  CardGridSurface,
  CollectionSelectionToggle,
  CollectionToolbar,
  CollectionViewToggle,
  DataTableSurface,
  EmptyCollectionState
} from '@renderer/shared/components/collection-scaffold';
import { CollectionTable, type CollectionSortState } from '@renderer/shared/components/collection-table';
import { SplitWorkspaceLayout } from '@renderer/shared/components/split-workspace';
import {
  FilterChip,
  IconButton,
  OverflowMenuButton,
  PanelCard,
  SectionHeader,
  TextField,
  SelectField,
  StatusBadge,
  ToolbarGroup
} from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { applyBulkContractPatch, deleteContracts, listContracts } from './contracts-repository';
import {
  selectContractCustomerOptions,
  selectContractProjectOptions,
  selectContractRows,
  selectContractSelectionItems,
  selectContractsSummary,
  type ContractTableRowViewModel
} from './contracts-selectors';
import { useContractsViewStore, type ContractsSort } from './contracts-view-store';

type PatchMode = 'unchanged' | 'clear' | 'set';
type SortDirection = 'asc' | 'desc';
type ContractBulkDraft = Omit<ContractBulkPatch, 'contractIds'>;

const contractStatuses: ContractStatus[] = ['draft', 'pending_review', 'ready_to_send', 'sent', 'signed', 'expired', 'archived'];

function createInitialContractBulkDraft(): ContractBulkDraft {
  return {
    status: { mode: 'unchanged' },
    projectId: { mode: 'unchanged' },
    signedAt: { mode: 'unchanged' },
    effectiveFrom: { mode: 'unchanged' },
    effectiveUntil: { mode: 'unchanged' }
  };
}

function formatDateInputValue(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function toIsoDate(value: string): string {
  return `${value}T12:00:00.000Z`;
}

function contractSortToColumnId(sort: ContractsSort): string | null {
  switch (sort) {
    case 'title':
      return 'title';
    case 'effective':
      return 'effective';
    default:
      return null;
  }
}

function contractColumnIdToSort(columnId: string): ContractsSort | null {
  switch (columnId) {
    case 'title':
      return 'title';
    case 'effective':
      return 'effective';
    default:
      return null;
  }
}

function getDefaultContractSortDirection(sort: ContractsSort): SortDirection {
  return sort === 'title' ? 'asc' : 'desc';
}

export function ContractsScreen(): JSX.Element {
  const { locale, t } = useI18n();
  const preferences = useShellStore((state) => state.preferences);
  const savePreferences = useShellStore((state) => state.savePreferences);
  const [collection, setCollection] = useState<ContractCollectionRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [contractBulkDraft, setContractBulkDraft] = useState<ContractBulkDraft>(createInitialContractBulkDraft);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const {
    statusFilter,
    customerFilter,
    projectFilter,
    sort,
    selectionSession,
    setStatusFilter,
    setCustomerFilter,
    setProjectFilter,
    setSort,
    setViewMode,
    syncSelectionQuery,
    toggleSelection,
    removeSelectedId,
    clearSelection
  } = useContractsViewStore();
  const [sortDirection, setSortDirection] = useState<SortDirection>(getDefaultContractSortDirection(sort));

  useEffect(() => {
    let active = true;

    setError(null);
    void listContracts()
      .then((nextCollection) => {
        if (active) {
          setCollection(nextCollection);
        }
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Contracts unavailable.');
        }
      });

    return () => {
      active = false;
    };
  }, [reloadToken]);

  useEffect(() => {
    return () => {
      clearSelection();
      setContractBulkDraft(createInitialContractBulkDraft());
    };
  }, [clearSelection]);

  useEffect(
    () =>
      subscribeToShellDomainInvalidations((detail) => {
        if (detail.domain === 'contracts') {
          setReloadToken((value) => value + 1);
        }
      }),
    []
  );

  const summary = useMemo(() => (collection ? selectContractsSummary(collection) : null), [collection]);
  const rows = useMemo(() => {
    if (!collection) {
      return [];
    }

    return selectContractRows(collection, {
      locale,
      statusFilter,
      customerFilter,
      projectFilter,
      sort,
      sortDirection,
      t
    });
  }, [collection, customerFilter, locale, projectFilter, sort, sortDirection, statusFilter, t]);
  const contractColumns = useMemo<CollectionColumnConfig[]>(
    () => [
      { id: 'selection', label: '', className: 'av-collection-table__selection-cell', locked: true },
      { id: 'title', label: t('contracts.table.title'), sortable: true, locked: true },
      { id: 'customer', label: t('contracts.table.customer') },
      { id: 'project', label: t('contracts.table.project') },
      { id: 'effective', label: t('contracts.summary.effectiveUntil'), sortable: true },
      { id: 'value', label: t('contracts.table.value') },
      { id: 'actions', label: '', align: 'right', locked: true }
    ],
    [t]
  );
  const visibleContractColumns = useMemo(
    () => resolveVisibleCollectionColumns(contractColumns, preferences.collectionColumnVisibility.contracts),
    [contractColumns, preferences.collectionColumnVisibility.contracts]
  );
  const selectionItems = useMemo(() => (collection ? selectContractSelectionItems(collection) : []), [collection]);
  const visibleIds = useMemo(() => rows.map((row) => row.id), [rows]);
  const visibleIdsKey = visibleIds.join('|');
  const queryKey = `${statusFilter}|${customerFilter}|${projectFilter}|${sort}|${sortDirection}|${preferences.contractsViewMode}`;

  useEffect(() => {
    syncSelectionQuery(queryKey, visibleIds);
  }, [queryKey, syncSelectionQuery, visibleIds, visibleIdsKey]);

  async function handleDeleteSelected(contractIds: string[]): Promise<void> {
    if (contractIds.length === 0) {
      return;
    }

    setMutationError(null);

    try {
      const nextCollection = await deleteContracts({ ids: contractIds });
      setCollection(nextCollection);
      clearSelection();
      setContractBulkDraft(createInitialContractBulkDraft());
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Contract delete failed.');
    }
  }

  useEffect(
    () =>
      subscribeToShellDeleteRequests((detail) => {
        if (detail.domain !== 'contracts' || detail.ids.length === 0) {
          return;
        }

        void handleDeleteSelected(detail.ids);
      }),
    [clearSelection]
  );

  if (error) {
    return (
      <div className="av-page">
        <EmptyCollectionState title={t('contracts.title')} description={error} />
      </div>
    );
  }

  if (!collection || !summary) {
    return <div className="av-page" />;
  }

  const selectedItems = selectionSession.selectedIds
    .map((selectedId) => selectionItems.find((item) => item.id === selectedId) ?? null)
    .filter((item): item is NonNullable<typeof item> => item !== null);
  const projectOptions = selectContractProjectOptions(collection, t);
  const quickAddContext = createQuickAddContext({
    customers: collection.customerSummaries,
    projects: collection.projectSummaries,
    t
  });
  const assignableProjectOptions = projectOptions.filter((option) => option.value !== 'all');
  const hasContractBulkDraftChanges = Object.values(contractBulkDraft).some((patch) => patch.mode !== 'unchanged');
  const isContractBulkDraftValid =
    (contractBulkDraft.projectId.mode !== 'set' || contractBulkDraft.projectId.value.length > 0) &&
    (contractBulkDraft.signedAt.mode !== 'set' || contractBulkDraft.signedAt.value.length > 0) &&
    (contractBulkDraft.effectiveFrom.mode !== 'set' || contractBulkDraft.effectiveFrom.value.length > 0) &&
    (contractBulkDraft.effectiveUntil.mode !== 'set' || contractBulkDraft.effectiveUntil.value.length > 0);
  const tableSortColumnId = contractSortToColumnId(sort);
  const tableSortState: CollectionSortState | undefined = tableSortColumnId
    ? {
        columnId: tableSortColumnId,
        direction: sortDirection
      }
    : undefined;

  function handleToggleSelection(contractId: string, event: MouseEvent<HTMLButtonElement>): void {
    event.preventDefault();
    event.stopPropagation();
    toggleSelection(contractId, visibleIds, queryKey, {
      additive: true,
      range: event.shiftKey
    });
  }

  function updateContractBulkDraft(patch: Partial<ContractBulkDraft>): void {
    setContractBulkDraft((current) => ({
      ...current,
      ...patch
    }));
  }

  async function handleApplyBulkChanges(): Promise<void> {
    if (selectedItems.length === 0 || !hasContractBulkDraftChanges || !isContractBulkDraftValid) {
      return;
    }

    setMutationError(null);

    try {
      const nextCollection = await applyBulkContractPatch({
        contractIds: selectionSession.selectedIds,
        ...contractBulkDraft
      });

      setCollection(nextCollection);
      clearSelection();
      setContractBulkDraft(createInitialContractBulkDraft());
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Contract update failed.');
    }
  }

  function handleTableSortChange(nextSortState: CollectionSortState): void {
    const nextSort = contractColumnIdToSort(nextSortState.columnId);

    if (!nextSort) {
      return;
    }

    setSort(nextSort);
    setSortDirection(nextSortState.direction);
  }

  function handleSortSelectChange(nextSort: ContractsSort): void {
    setSort(nextSort);
    setSortDirection(getDefaultContractSortDirection(nextSort));
  }

  function handleVisibleColumnIdsChange(columnIds: string[]): void {
    void savePreferences({
      collectionColumnVisibility: {
        ...preferences.collectionColumnVisibility,
        contracts: columnIds
      }
    });
  }

  function handleResetVisibleColumns(): void {
    const nextVisibility = { ...preferences.collectionColumnVisibility };
    delete nextVisibility.contracts;
    void savePreferences({ collectionColumnVisibility: nextVisibility });
  }

  function handleNullablePatchModeChange(
    mode: PatchMode,
    fallbackValue: string,
    field: 'projectId' | 'signedAt' | 'effectiveFrom' | 'effectiveUntil'
  ): void {
    if (mode === 'set') {
      updateContractBulkDraft({ [field]: { mode: 'set', value: fallbackValue } });
      return;
    }

    updateContractBulkDraft({ [field]: { mode } });
  }

  function renderContractCell(row: ContractTableRowViewModel, columnId: string): JSX.Element {
    const selected = selectionSession.selectedIds.includes(row.id);

    switch (columnId) {
      case 'selection':
        return (
          <td key={columnId} className="av-collection-table__cell av-collection-table__selection-cell">
            <CollectionSelectionToggle
              selected={selected}
              icon="contracts"
              label={row.title}
              onClick={(event) => handleToggleSelection(row.id, event)}
            />
          </td>
        );
      case 'title':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <div className="av-collection-table__identity-copy">
              <strong>
                <Link className="av-inline-link" to={`/contracts/${row.id}`}>
                  <span>{row.title}</span>
                </Link>
              </strong>
              <div className="av-inline-metadata">
                <span>{row.contractNumber}</span>
                <StatusBadge label={row.statusLabel} tone={row.statusTone} />
              </div>
            </div>
          </td>
        );
      case 'customer':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <div className="av-summary-stack">
              <strong>{row.customerName}</strong>
              <span>{row.issuerName}</span>
            </div>
          </td>
        );
      case 'project':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <div className="av-summary-stack">
              <strong>{row.projectName}</strong>
              <span>{row.effectiveLabel}</span>
            </div>
          </td>
        );
      case 'effective':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {row.effectiveLabel}
          </td>
        );
      case 'value':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {row.valueLabel}
          </td>
        );
      case 'actions':
        return (
          <td key={columnId} className="av-collection-table__cell is-right">
            <OverflowMenuButton onClick={(event) => event.stopPropagation()} />
          </td>
        );
      default:
        return <td key={columnId} className="av-collection-table__cell" />;
    }
  }

  return (
    <div className="av-page">
      <SectionHeader
        title={t('contracts.title')}
        subtitle={`${t('contracts.summary.active', { count: summary.activeCount })} • ${t('contracts.summary.archived', {
          count: summary.archivedCount
        })}`}
      />

      <InlineQuickAddPanel domain="contracts" context={quickAddContext} onCreated={async () => setCollection(await listContracts())} />

      <CollectionToolbar
        primary={
          <ToolbarGroup>
            {(['all', 'draft', 'pending_review', 'ready_to_send', 'signed', 'archived'] as const).map((filter) => (
              <FilterChip
                key={filter}
                active={statusFilter === filter}
                label={t(`contracts.filters.${filter}`)}
                onClick={() => setStatusFilter(filter)}
              />
            ))}
          </ToolbarGroup>
        }
        secondary={
          <ToolbarGroup>
            <SelectField
              options={selectContractCustomerOptions(collection, t)}
              value={customerFilter}
              onChange={(event) => setCustomerFilter(event.target.value)}
            />
            <SelectField
              options={projectOptions}
              value={projectFilter}
              onChange={(event) => setProjectFilter(event.target.value)}
            />
            <SelectField
              options={[
                { value: 'recent', label: t('contracts.sort.recent') },
                { value: 'effective', label: t('contracts.sort.effective') },
                { value: 'title', label: t('contracts.sort.title') }
              ]}
              value={sort}
              onChange={(event) => handleSortSelectChange(event.target.value as ContractsSort)}
            />
            <CollectionViewToggle
              options={[
                { value: 'list', label: t('common.view.list'), icon: 'list' },
                { value: 'grid', label: t('common.view.grid'), icon: 'grid' }
              ]}
              value={preferences.contractsViewMode}
              onChange={(value) => void setViewMode(value)}
            />
            <IconButton
              icon="settings"
              label={t('common.columns.settingsButton')}
              onClick={() => setColumnSettingsOpen(true)}
            />
          </ToolbarGroup>
        }
      />

      {mutationError ? <div className="av-inline-error">{mutationError}</div> : null}

      <SplitWorkspaceLayout
        railMode="fixed"
        main={
          rows.length === 0 ? (
            <EmptyCollectionState title={t('contracts.title')} description={t('common.noResults')} />
          ) : preferences.contractsViewMode === 'grid' ? (
            <CardGridSurface mode="grid">
              {rows.map((row) => {
                const selected = selectionSession.selectedIds.includes(row.id);

                return (
                  <PanelCard
                    key={row.id}
                    actions={
                      <OverflowMenuButton
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      />
                    }
                    className={['av-collection-card', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}
                  >
                    <div className="av-project-card__header">
                      <div className="av-project-card__identity">
                        <div className="av-project-card__icon-wrap">
                          <CollectionSelectionToggle
                            selected={selected}
                            className="av-card-selection-toggle"
                            icon="contracts"
                            label={row.title}
                            data-testid="av-card-selection-toggle"
                            onClick={(event) => handleToggleSelection(row.id, event)}
                          />
                        </div>
                        <div className="av-collection-table__identity-copy">
                          <strong>
                            <Link className="av-inline-link" to={`/contracts/${row.id}`}>
                              <span>{row.title}</span>
                            </Link>
                          </strong>
                          <div className="av-inline-metadata">
                            <span>{row.contractNumber}</span>
                            <StatusBadge label={row.statusLabel} tone={row.statusTone} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="av-project-card__footer">
                      <div className="av-summary-stack">
                        <strong>{row.customerName}</strong>
                        <span>{row.projectName}</span>
                      </div>
                      <div className="av-summary-stack">
                        <strong>{row.valueLabel}</strong>
                        <span>{row.effectiveLabel}</span>
                      </div>
                    </div>
                  </PanelCard>
                );
              })}
            </CardGridSurface>
          ) : (
            <DataTableSurface>
              <CollectionTable
                ariaLabel={t('contracts.title')}
                columns={visibleContractColumns}
                sortState={tableSortState}
                onSortChange={handleTableSortChange}
              >
                {rows.map((row) => {
                  const selected = selectionSession.selectedIds.includes(row.id);

                  return (
                    <tr
                      key={row.id}
                      className={['av-collection-table__row', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}
                    >
                      {visibleContractColumns.map((column) => renderContractCell(row, column.id))}
                    </tr>
                  );
                })}
              </CollectionTable>
            </DataTableSurface>
          )
        }
        rail={
          selectedItems.length > 0 ? (
            <BulkEditorRail
              title={t('contracts.selection.title', { count: selectedItems.length })}
              hiddenSelectedCount={selectionSession.hiddenSelectedCount}
              items={selectedItems.map((item) => ({
                id: item.id,
                icon: 'contracts',
                label: item.label,
                subtitle: item.subtitle
              }))}
              onClose={() => {
                clearSelection();
                setContractBulkDraft(createInitialContractBulkDraft());
              }}
              onRemoveItem={(itemId) => removeSelectedId(itemId, visibleIds)}
              destructiveAction={{
                label: t('shell.delete'),
                onClick: () => void handleDeleteSelected(selectionSession.selectedIds)
              }}
              primaryAction={{
                label: t('common.applyChanges'),
                disabled: !hasContractBulkDraftChanges || !isContractBulkDraftValid,
                onClick: () => void handleApplyBulkChanges()
              }}
              secondaryAction={{
                label: t('common.cancel'),
                onClick: () => {
                  clearSelection();
                  setContractBulkDraft(createInitialContractBulkDraft());
                }
              }}
            >
              <div className="av-bulk-editor-form">
                <label className="av-form-field">
                  <span>{t('contracts.fields.status')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      ...contractStatuses.map((status) => ({
                        value: status,
                        label: t(`enums.contractStatus.${status}`)
                      }))
                    ]}
                    value={contractBulkDraft.status.mode === 'set' ? contractBulkDraft.status.value : 'unchanged'}
                    onChange={(event) =>
                      updateContractBulkDraft({
                        status:
                          event.target.value === 'unchanged'
                            ? { mode: 'unchanged' }
                            : { mode: 'set', value: event.target.value as ContractStatus }
                      })
                    }
                  />
                </label>

                <label className="av-form-field">
                  <span>{t('contracts.fields.project')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'clear', label: t('common.clearValue') },
                      { value: 'set', label: t('common.setValue') }
                    ]}
                    value={contractBulkDraft.projectId.mode}
                    onChange={(event) =>
                      handleNullablePatchModeChange(event.target.value as PatchMode, assignableProjectOptions[0]?.value ?? '', 'projectId')
                    }
                  />
                  {contractBulkDraft.projectId.mode === 'set' ? (
                    <SelectField
                      options={assignableProjectOptions}
                      value={contractBulkDraft.projectId.value}
                      onChange={(event) => updateContractBulkDraft({ projectId: { mode: 'set', value: event.target.value } })}
                    />
                  ) : null}
                </label>

                {(['signedAt', 'effectiveFrom', 'effectiveUntil'] as const).map((field) => (
                  <label key={field} className="av-form-field">
                    <span>
                      {field === 'signedAt'
                        ? t('contracts.summary.signature')
                        : field === 'effectiveFrom'
                          ? t('contracts.summary.effectiveFrom')
                          : t('contracts.summary.effectiveUntil')}
                    </span>
                    <SelectField
                      options={[
                        { value: 'unchanged', label: t('common.leaveUnchanged') },
                        { value: 'clear', label: t('common.clearValue') },
                        { value: 'set', label: t('common.setValue') }
                      ]}
                      value={contractBulkDraft[field].mode}
                      onChange={(event) => handleNullablePatchModeChange(event.target.value as PatchMode, new Date().toISOString(), field)}
                    />
                    {contractBulkDraft[field].mode === 'set' ? (
                      <TextField
                        type="date"
                        value={formatDateInputValue(contractBulkDraft[field].value)}
                        onValueChange={(value) =>
                          updateContractBulkDraft({
                            [field]: { mode: 'set', value: value ? toIsoDate(value) : '' }
                          })
                        }
                      />
                    ) : null}
                  </label>
                ))}
              </div>
            </BulkEditorRail>
          ) : null
        }
      />

      <CollectionColumnSettingsModal
        columns={contractColumns}
        domain="contracts"
        isOpen={columnSettingsOpen}
        strings={{
          title: t('common.columns.settingsTitle'),
          description: t('common.columns.settingsDescription'),
          reset: t('common.columns.settingsReset'),
          close: t('common.close'),
          locked: t('common.columns.settingsLocked')
        }}
        visibleColumnIds={preferences.collectionColumnVisibility.contracts}
        onClose={() => setColumnSettingsOpen(false)}
        onReset={handleResetVisibleColumns}
        onVisibleColumnIdsChange={handleVisibleColumnIdsChange}
      />
    </div>
  );
}
